import { useEffect, useState, type ReactNode } from "react";
import { CodeEditor } from "@/components/codeEditor";
import { fetchSignedText } from "@/http";
import { createLogger } from "@/logging";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatBytes } from "@/utils";
import { resolveLoadErrorMessage } from "../../loadState";
import { formatTextForDisplay, type FilePreviewMode } from "./filePreviewFormat";
import styles from "../FileBrowser.module.css";

const logger = createLogger("components/dataset/fileBrowser/preview");

type PreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; text: string }
  | { status: "error"; message: string };

/**
 * 파일 한 건의 본문 미리보기. 판정된 표현(`mode`)을 그린다.
 *
 * extra/meta 파일과 canonical 파일은 같은 자리에서 같게 보여야 한다. 그래서 판정은 호출한 pane 이
 * 아니라 `filePreviewFormat` 하나가 소유한다. 그릴 수 없는 파일(미지원 포맷·상한 초과·로드 실패)은
 * 빈 화면 대신 이유를 표시한다. 다운로드 경로는 호출한 pane 이 소유한다.
 *
 * `resolveUrl` 은 1회용 signed GET URL 을 주는 thunk다. 발급 경로가 출처마다 다르다(extra/meta
 * endpoint vs 목록 인라인 URL). 만료 시 재발급도 그 경로가 맡는다.
 */
export function FilePreviewBody({
  name,
  fileUuid,
  mode,
  resolveUrl,
}: {
  name: string;
  // 본문·이미지 재조회의 키. 폴더가 다르면 파일명이 같을 수 있어서 파일명은 키가 될 수 없다
  // (`metadata/wrist_left/ep_000000.json` 과 `.../wrist_right/ep_000000.json`).
  fileUuid: string;
  mode: FilePreviewMode;
  resolveUrl: () => Promise<string>;
}): ReactNode {
  const isImage = mode.kind === "image";
  const showsText = mode.kind === "text";

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [text, setText] = useState<PreviewState>({ status: "idle" });

  // 이미지면 표시용 signed URL 을 한 번 받아 <img src> 에 설정한다(파일 교체 시 다시 요청).
  useEffect(() => {
    if (!isImage) {
      setImageUrl(null);
      return;
    }
    let cancelled = false;
    setImageUrl(null);
    void resolveUrl()
      .then((url) => {
        if (!cancelled) setImageUrl(url);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error("file url (image) failed", error);
      });
    return () => {
      cancelled = true;
    };
    // resolveUrl 은 매 렌더 새 클로저이므로 의존성에서 제외한다. fileUuid+isImage 가 실제 키다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUuid, isImage]);

  // 텍스트성 파일은 본문을 직접 받아 편집기에 읽기 전용으로 표시한다.
  useEffect(() => {
    if (!showsText) {
      setText({ status: "idle" });
      return;
    }
    const controller = new AbortController();
    setText({ status: "loading" });
    void resolveUrl()
      .then((url) => fetchSignedText(url, controller.signal))
      .then((body) => {
        if (controller.signal.aborted) return;
        setText({ status: "success", text: body });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        logger.error("file body fetch failed", error);
        setText({ status: "error", message: resolveLoadErrorMessage(error) });
      });
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUuid, showsText]);

  if (mode.kind === "image") {
    return (
      <div className={styles.imageFrame}>
        {imageUrl ? (
          <img className={styles.image} src={imageUrl} alt={name} />
        ) : (
          <LoadingState mode="plain">이미지를 불러오고 있습니다.</LoadingState>
        )}
      </div>
    );
  }

  if (mode.kind === "none") {
    return null;
  }

  if (mode.kind === "textTooLarge") {
    return (
      <EmptyState>
        {`파일이 커서(${formatBytes(mode.sizeBytes)}) 미리보기를 건너뜁니다. 내려받아 확인해 주세요.`}
      </EmptyState>
    );
  }

  if (text.status === "loading" || text.status === "idle") {
    return <LoadingState>내용을 불러오고 있습니다.</LoadingState>;
  }
  if (text.status === "error") {
    return <EmptyState tone="error">{text.message}</EmptyState>;
  }

  return (
    <div className={styles.textFrame}>
      <CodeEditor
        value={formatTextForDisplay(text.text, mode.language)}
        onChange={() => undefined}
        readOnly
        language={mode.language}
        ariaLabel={`${name} 내용`}
      />
    </div>
  );
}
